import { useEffect, useRef } from 'react';
import type { ChatMessage } from '../types/foreman';
import type { UserRole } from '../types/gateway';
import type { CorrectionFormData } from './training';
import { UserMessage, ForemanMessage, SystemMessage } from './messages';
import { QuickActions } from './QuickActions';
import { getRoleConfig } from '../config/roles';
import styles from './ChatArea.module.css';

interface ChatAreaProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  role: UserRole;
  userName?: string;
  onCorrect?: (messageId: string, data: CorrectionFormData) => void;
  onAction?: (action: string) => void;
  /** Selected when a quick-action card is clicked from the empty state. */
  onQuickAction?: (prompt: string) => void;
}

function firstName(name?: string): string {
  if (!name) return 'there';
  return name.split(' ')[0] || name;
}

export function ChatArea({
  messages,
  isStreaming,
  role,
  userName,
  onCorrect,
  onAction,
  onQuickAction,
}: ChatAreaProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const cfg = getRoleConfig(role);
  const headline = cfg.welcomeHeadline.replace('{name}', firstName(userName));

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  const isEmpty = messages.length === 0;

  return (
    <div
      className={`${styles.chatArea} ${isEmpty ? styles.chatAreaEmpty : ''}`}
      data-accent={cfg.accent}
    >
      <div className={`${styles.messages} ${isEmpty ? styles.messagesEmpty : ''}`}>
        {isEmpty && (
          <div className={styles.welcome}>
            <div className={styles.welcomeBadge}>
              <span className={styles.welcomeIcon}>{cfg.icon}</span>
              <span className={styles.welcomeBadgeLabel}>{cfg.label}</span>
            </div>
            <h1 className={styles.welcomeTitle}>{headline}</h1>
            <p className={styles.welcomeSub}>{cfg.welcomeSubline}</p>
            <p className={styles.welcomeHint}>
              Ask a question, drop in a scope, or pick a quick action below.
            </p>
            {onQuickAction && (
              <QuickActions role={role} onSelect={onQuickAction} />
            )}
          </div>
        )}
        {messages.map((msg) => {
          if (msg.type === 'user') {
            return <UserMessage key={msg.id} message={msg} />;
          }
          if (msg.type === 'foreman') {
            return (
              <ForemanMessage
                key={msg.id}
                message={msg}
                onCorrect={onCorrect}
                onAction={onAction}
              />
            );
          }
          if (msg.type === 'system') {
            return <SystemMessage key={msg.id} message={msg} />;
          }
          return null;
        })}
        {isStreaming && (
          <div className={styles.typing}>
            <span className={styles.typingDot} />
            <span className={styles.typingDot} />
            <span className={styles.typingDot} />
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
